/* Nutriheal theme — product page: variant switching by section re-render, gallery,
   sticky purchase bar and share. Loaded with defer from sections/main-product.liquid. */
(() => {
  'use strict';
  if (window.NutrihealProductInitialized) return;
  window.NutrihealProductInitialized = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let controller;
  let sequence = 0;
  let stickyObserver;

  /* Gallery ----------------------------------------------------------------- */
  function initGallery(root) {
    if (!root || root.dataset.galleryReady) return;
    const track = root.querySelector('[data-gallery-track]');
    const slides = [...root.querySelectorAll('[data-gallery-slide]')];
    if (!track || slides.length < 2) return;
    root.dataset.galleryReady = 'true';
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    const counter = root.querySelector('[data-gallery-counter]');
    const positionTemplate = track.dataset.positionTemplate || '';
    const isScroller = () => track.scrollWidth > track.clientWidth + 1;
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    function activate(next, { scroll = true, instant = false, announce = true } = {}) {
      index = next;
      slides.forEach((slide, n) => slide.classList.toggle('is-active', n === index));
      thumbs.forEach((thumb, n) => {
        if (n === index) thumb.setAttribute('aria-current', 'true'); else thumb.removeAttribute('aria-current');
      });
      if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
      const lazy = slides[index].querySelector('img[loading="lazy"]');
      if (lazy) lazy.loading = 'eager';
      if (scroll && isScroller()) {
        track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: instant || reducedMotion.matches ? 'auto' : 'smooth' });
      }
      thumbs[index]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      if (announce) {
        // The visual counter is aria-hidden; give assistive tech the same "n of total" plus the media label.
        const label = thumbs[index]?.getAttribute('aria-label') || '';
        const position = positionTemplate.replace('[number]', String(index + 1)).replace('[total]', String(slides.length));
        window.Nutriheal?.announce([label, position].filter(Boolean).join('. '));
      }
    }

    thumbs.forEach((thumb, n) => thumb.addEventListener('click', (event) => {
      event.preventDefault(); // Thumbs are #Slide links so they also work without JS; here we swap in place.
      activate(n);
    }));
    let frame;
    track.addEventListener('scroll', () => {
      if (!isScroller()) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const width = slides[0].offsetWidth || 1;
        const next = Math.min(slides.length - 1, Math.max(0, Math.round(track.scrollLeft / width)));
        if (next !== index) activate(next, { scroll: false });
      });
    }, { passive: true });
    if (index > 0) activate(index, { instant: true, announce: false });
  }

  /* Sticky purchase bar (mobile) ------------------------------------------- */
  function setupSticky() {
    stickyObserver?.disconnect();
    const form = document.querySelector('[data-product-section] [data-product-form]');
    const sticky = document.querySelector('[data-sticky-purchase]');
    document.body.classList.remove('has-sticky-purchase');
    if (!form || !sticky || !('IntersectionObserver' in window)) return;
    stickyObserver = new IntersectionObserver(([entry]) => {
      const hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      sticky.hidden = hidden;
      // product.css reserves the bar's height under the page while the class is present.
      document.body.classList.toggle('has-sticky-purchase', !hidden);
    }, { threshold: 0 });
    stickyObserver.observe(form);
  }

  /* Share --------------------------------------------------------------------- */
  async function share(button) {
    const data = { title: button.dataset.shareTitle, url: button.dataset.shareUrl || location.href };
    try {
      if (navigator.share) { await navigator.share(data); return; }
      await navigator.clipboard.writeText(data.url);
      window.Nutriheal?.announce(button.dataset.shareCopied || '');
    } catch { /* The user cancelled or the clipboard is unavailable; nothing to recover. */ }
  }

  /* Variant switching: fetch the section for the new URL and swap the purchase UI ---- */
  async function changeVariant(link, section) {
    if (document.body.dataset.cartBusy === 'true') return;
    controller?.abort();
    controller = new AbortController();
    const current = ++sequence;
    const destination = new URL(link.href);
    const renderUrl = new URL(destination);
    renderUrl.searchParams.set('section_id', section.dataset.sectionId);
    const quantity = section.querySelector('[name="quantity"]')?.value;
    const optionId = link.dataset.optionId;
    // Disable the stale variant immediately; it must never be submitted while resolving.
    section.setAttribute('aria-busy', 'true');
    section.querySelectorAll('button[type="submit"]').forEach((button) => { button.disabled = true; });
    try {
      const response = await fetch(renderUrl, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Variant unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('[data-product-section]');
      if (!fresh) throw new Error('Missing product section');
      if (current !== sequence) return;
      // Only replace the theme-owned purchase UI; merchant app blocks keep their lifecycle.
      ['.product-purchase', '[data-sticky-purchase]', '[data-product-gallery]'].forEach((selector) => {
        const old = section.querySelector(selector);
        const replacement = fresh.querySelector(selector);
        if (old && replacement) old.replaceWith(replacement);
      });
      const input = section.querySelector('[name="quantity"]');
      if (input && quantity) { input.value = quantity; if (!input.checkValidity()) input.value = input.min || '1'; }
      history.replaceState({}, '', destination);
      section.removeAttribute('aria-busy');
      const candidates = [...section.querySelectorAll('[data-option-link]')];
      const focus = optionId ? candidates.find((item) => item.dataset.optionId === optionId) : candidates.find((item) => item.href === destination.href);
      focus?.focus({ preventScroll: true });
      initGallery(section.querySelector('[data-product-gallery]'));
      setupSticky();
      // The price's aria-live region was just replaced wholesale, so it does not fire on its own:
      // announce price and stock together.
      const announcement = [section.querySelector('.product-price')?.textContent, section.querySelector('.stock')?.textContent]
        .map((text) => (text || '').replace(/\s+/g, ' ').trim())
        .filter(Boolean)
        .join('. ');
      window.Nutriheal?.announce(announcement);
      if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
      section.dispatchEvent(new CustomEvent('nutriheal:variant-change', { bubbles: true, detail: { variantId: section.querySelector('[name="id"]')?.value } }));
    } catch (error) {
      if (error.name !== 'AbortError' && current === sequence) location.assign(destination);
    }
  }

  document.addEventListener('click', (event) => {
    const shareButton = event.target.closest('[data-share-product]');
    if (shareButton) { share(shareButton); return; }
    const link = event.target.closest('[data-option-link]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    // Combined listings use a normal navigation so every dependent section/app updates.
    if (link.hasAttribute('data-product-change')) return;
    const section = link.closest('[data-product-section]');
    if (!section) return;
    event.preventDefault();
    changeVariant(link, section);
  });

  function init() {
    document.querySelectorAll('[data-product-gallery]').forEach(initGallery);
    setupSticky();
  }
  init();
  document.addEventListener('shopify:section:load', init);
})();
